<script setup lang="ts">
import { computed } from "vue";
import type { Team } from "@shared/types";
import { ROUND_LABELS } from "@shared/types";
import { teamPoints } from "@shared/scoring";
import Flag from "../common/Flag.vue";

const props = defineProps<{
  team: Team;
  draft: Team;
  dirty: boolean;
  invalid: boolean;
  saving?: boolean;
}>();
const emit = defineEmits<{ (e: "update", field: keyof Team, value: number): void }>();

const livePoints = computed(() => teamPoints(props.draft));

const numericFields: Array<{ key: keyof Team; label: string }> = [
  { key: "wins", label: "W" },
  { key: "draws", label: "D" },
  { key: "goals_for", label: "GF" },
  { key: "goals_against", label: "GA" },
  { key: "clean_sheets", label: "CS" },
  { key: "yellows", label: "Y" },
  { key: "reds", label: "R" },
];

const roundOptions = [0, 1, 2, 3, 4, 5, 6] as const;

function update(field: keyof Team, raw: string | number) {
  const n = typeof raw === "number" ? raw : parseInt(raw, 10);
  if (Number.isFinite(n) && n >= 0) emit("update", field, n);
}
</script>

<template>
  <tr
    :class="[
      'border-b border-slate-100 hover:bg-slate-50/60 transition-colors',
      props.dirty && !props.invalid && 'bg-amber-50',
      props.invalid && 'bg-red-50 outline outline-1 outline-red-400',
    ]"
  >
    <td class="px-3 py-2 whitespace-nowrap">
      <div class="flex items-center gap-2">
        <Flag :code="props.team.flag_code" :name="props.team.name" size="sm" />
        <span class="font-medium text-slate-800">{{ props.team.name }}</span>
      </div>
    </td>
    <td v-for="f in numericFields" :key="f.key" class="px-2 py-2">
      <input
        type="number"
        min="0"
        :value="props.draft[f.key]"
        :disabled="props.saving"
        @input="(ev) => update(f.key, (ev.target as HTMLInputElement).value)"
        class="w-14 bg-transparent text-center tabular text-slate-900 rounded focus:outline-none focus:bg-white focus:ring-2 focus:ring-pot1/40"
      />
    </td>
    <td class="px-2 py-2">
      <select
        :value="props.draft.round_reached"
        :disabled="props.saving"
        @change="(ev) => update('round_reached', (ev.target as HTMLSelectElement).value)"
        class="bg-white border border-slate-300 rounded px-2 py-1 text-sm text-slate-800 shadow-sm focus:outline-none focus:border-pot1 focus:ring-1 focus:ring-pot1/40"
      >
        <option v-for="r in roundOptions" :key="r" :value="r">{{ ROUND_LABELS[r] }}</option>
      </select>
    </td>
    <td class="px-2 py-2 text-center">
      <input
        type="checkbox"
        :checked="props.draft.eliminated === 1"
        :disabled="props.saving"
        @change="(ev) => update('eliminated', (ev.target as HTMLInputElement).checked ? 1 : 0)"
        class="h-4 w-4 rounded border-slate-300 text-rose-500 focus:ring-rose-400/40"
        title="Eliminated from the tournament"
      />
    </td>
    <td class="px-3 py-2 tabular font-bold text-slate-900">
      {{ livePoints }}<span v-if="props.dirty" class="ml-1 text-amber-500">✏️</span>
    </td>
  </tr>
</template>
